import { useMemo } from "react";

import { useLocalization } from "@fluent/react";
import { TableSelect } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import {
  useCorrectTransactionSubCategory,
  useIsCorrectingTaxonomy,
} from "../../hooks/useCorrectTransactionCategory";

import type { TransactionCategory } from "../../hooks/useTransactionCategories";

const NO_SUB_CATEGORY = "__no_sub_category__";

interface SubCategorySelectProps {
  transaction: API.Transaction;
  categories: TransactionCategory[];
  selectId: string;
}

const SubCategorySelect = ({
  transaction,
  categories,
  selectId,
}: SubCategorySelectProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();

  const { mutate } = useCorrectTransactionSubCategory({
    transactionId: transaction.id,
    onError: () => {
      sendNotification({
        type: "negative",
        text: l10n.getString("live-ledger-subcategory-save-error"),
      });
    },
  });
  // A pending category write decides which children this select may offer, so it also disables
  // this control, not only its own.
  const isCorrecting = useIsCorrectingTaxonomy(transaction.id);

  const options = useMemo(() => {
    const parent = categories.find(
      (category) => category.label === transaction.category
    );
    // Copy before sorting, since this array is the query cache's.
    return [...(parent?.sub_categories ?? [])].sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [categories, transaction.category]);

  const currentSlug = options.find(
    (option) => option.label === transaction.sub_category
  )?.slug;

  const handleChange = (slug: string) => {
    if (slug === (currentSlug ?? NO_SUB_CATEGORY)) return;

    if (slug === NO_SUB_CATEGORY) {
      mutate({ slug: "", label: "" });
      return;
    }

    const picked = options.find((option) => option.slug === slug);
    if (!picked) return;
    mutate({ slug: picked.slug, label: picked.label });
  };

  const items = [
    <TableSelect.Item key={NO_SUB_CATEGORY} value={NO_SUB_CATEGORY}>
      {l10n.getString("live-ledger-subcategory-none")}
    </TableSelect.Item>,
    ...options.map((option) => (
      <TableSelect.Item key={option.slug} value={option.slug}>
        {option.label}
      </TableSelect.Item>
    )),
  ];

  return (
    <TableSelect
      id={selectId}
      label={l10n.getString("live-ledger-subcategory-select-label")}
      value={currentSlug ?? NO_SUB_CATEGORY}
      onChange={handleChange}
      isDisabled={isCorrecting}
    >
      {items}
    </TableSelect>
  );
};

export default SubCategorySelect;
